import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/lib/auth";
import { reproducirSonidoNotificacion } from "@/lib/sonido";
import {
  useDispositivosMesera,
  useAprobarDispositivo,
  useRechazarDispositivo,
} from "@/lib/queries/dispositivos-mesera";
import type { DispositivoMeseraRespuesta } from "@tentacion/shared";

/** Resumen corto y legible del navegador/dispositivo a partir del User-Agent crudo. */
function resumirDispositivo(userAgent: string | null): string {
  if (!userAgent) return "Dispositivo desconocido";
  const es = (s: string) => userAgent.includes(s);
  const sistema = es("iPad")
    ? "iPad"
    : es("iPhone")
      ? "iPhone"
      : es("Android")
        ? "Android"
        : es("Windows")
          ? "Windows"
          : es("Macintosh")
            ? "Mac"
            : "Dispositivo";
  const navegador = es("Edg/") ? "Edge" : es("Chrome/") ? "Chrome" : es("Firefox/") ? "Firefox" : es("Safari/") ? "Safari" : "";
  return navegador ? `${sistema} · ${navegador}` : sistema;
}

/**
 * Aviso emergente para el Administrador cuando una mesera intenta entrar por
 * PIN desde un dispositivo/IP nuevo — antes había que acordarse de revisar
 * Admin → Dispositivos de Meseras manualmente; ahora salta solo, con
 * aprobar/rechazar directo desde el mismo aviso.
 */
export default function NotificadorDispositivosMesera() {
  const { usuario } = useAuth();
  const esAdmin = usuario?.rol === "ADMIN";

  const { data: dispositivos } = useDispositivosMesera(esAdmin);
  const aprobar = useAprobarDispositivo();
  const rechazar = useRechazarDispositivo();

  const [destacado, setDestacado] = useState<DispositivoMeseraRespuesta | null>(null);
  const [descartados, setDescartados] = useState<Set<string>>(new Set());
  const idsVistos = useRef<Set<string>>(new Set());
  const esPrimeraCarga = useRef(true);

  const pendientes = (dispositivos ?? []).filter((d) => d.estado === "PENDIENTE" && !descartados.has(d.id));

  useEffect(() => {
    if (!esAdmin || !dispositivos) return;

    const nuevos = dispositivos.filter((d) => d.estado === "PENDIENTE" && !idsVistos.current.has(d.id));
    dispositivos.forEach((d) => idsVistos.current.add(d.id));

    if (!esPrimeraCarga.current && nuevos.length > 0 && nuevos[0]) {
      reproducirSonidoNotificacion();
      setDestacado(nuevos[0]);
    }
    esPrimeraCarga.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispositivos, esAdmin]);

  if (!esAdmin) return null;

  const procesando = aprobar.isPending || rechazar.isPending;

  const manejarAprobar = (id: string) => {
    aprobar.mutate(id, { onSuccess: () => setDestacado(null) });
  };
  const manejarRechazar = (id: string) => {
    rechazar.mutate(id, { onSuccess: () => setDestacado(null) });
  };
  const manejarDescartar = (id: string) => {
    setDescartados((prev) => new Set(prev).add(id));
    setDestacado(null);
  };

  return (
    <>
      {/* ALERTA FLOTANTE EMERGENTE — dispositivo nuevo detectado */}
      {destacado && (
        <div className="fixed top-5 right-5 z-[100] w-full max-w-sm overflow-hidden rounded-3xl bg-superficie border-2 border-amber-500/40 p-4 shadow-2xl backdrop-blur-xl animate-in slide-in-from-top-4 duration-300">
          <div className="flex items-start gap-3">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-amber-500/15 text-2xl shadow-inner border border-amber-500/30">
              📱
            </div>

            <div className="flex-1 min-w-0 space-y-1">
              <div className="flex items-center justify-between gap-2">
                <span className="inline-flex items-center gap-1 rounded-lg px-2.5 py-0.5 text-[10px] font-black uppercase tracking-wider bg-amber-500 text-white">
                  ⏳ Dispositivo nuevo
                </span>
                <button
                  type="button"
                  onClick={() => manejarDescartar(destacado.id)}
                  className="flex h-7 w-7 items-center justify-center text-texto-secundario hover:text-texto text-xs font-bold"
                >
                  ✕
                </button>
              </div>

              <p className="font-display text-base font-black text-texto">{destacado.usuario.nombre}</p>
              <p className="text-xs text-texto-secundario">
                📍 IP: <span className="font-mono font-semibold text-texto">{destacado.ip}</span>
                {" · "}
                {resumirDispositivo(destacado.userAgent)}
              </p>

              <div className="pt-2 flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => manejarRechazar(destacado.id)}
                  disabled={procesando}
                  className="flex-1 py-2 px-3 text-xs font-black text-red-700 bg-red-50 border border-red-200 rounded-xl hover:bg-red-100 transition disabled:opacity-50"
                >
                  ✕ Rechazar
                </button>
                <button
                  type="button"
                  onClick={() => manejarAprobar(destacado.id)}
                  disabled={procesando}
                  className="flex-1 py-2 px-3 text-xs font-black text-white bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 rounded-xl shadow-md transition-all disabled:opacity-50"
                >
                  ✓ Aprobar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* BOTÓN FLOTANTE INDICADOR — sigue habiendo pendientes aunque se cierre la alerta */}
      {pendientes.length > 0 && !destacado && (
        <button
          type="button"
          onClick={() => setDestacado(pendientes[0] ?? null)}
          className="fixed bottom-6 right-6 z-[90] flex items-center gap-2.5 rounded-full bg-gradient-to-r from-amber-500 via-orange-500 to-amber-600 px-4 py-3 text-xs font-black text-white shadow-xl shadow-amber-600/30 transition-all hover:scale-105 active:scale-95 border border-amber-300/30 animate-bounce"
        >
          <span className="text-base">📱</span>
          <span>
            {pendientes.length} Dispositivo{pendientes.length > 1 ? "s" : ""} Pendiente{pendientes.length > 1 ? "s" : ""}
          </span>
          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-white text-amber-700 text-[10px] font-black">
            {pendientes.length}
          </span>
        </button>
      )}
    </>
  );
}
